import React, { useState } from 'react'
import { withRouter } from 'react-router'
import { compose } from 'recompose'
import { filter } from 'lodash'

import Paper from '@material-ui/core/Paper'
import IconButton from '@material-ui/core/IconButton'
import CircularProgress from '@material-ui/core/CircularProgress'
import DeleteIcon from '@material-ui/icons/Delete'
import AddIcon from '@material-ui/icons/AddCircle'
import EditIcon from '@material-ui/icons/Edit'
import SaveIcon from '@material-ui/icons/Save'
import CancelIcon from '@material-ui/icons/Cancel'

import {
  MuiThemeProvider,
  createMuiTheme,
  withStyles
} from '@material-ui/core/styles'
import { theme } from '../../theme'

import {
  Grid,
  Table,
  TableEditRow,
  TableEditColumn,
  TableHeaderRow,
  TableBandHeader,
  TableGroupRow,
  Toolbar,
  SearchPanel,
  TableRowDetail,
  PagingPanel,
  TableSelection
} from '@devexpress/dx-react-grid-material-ui'

import {
  DataTypeProvider,
  GroupingState,
  IntegratedGrouping,
  SortingState,
  IntegratedSorting,
  SearchState,
  IntegratedFiltering,
  RowDetailState,
  EditingState,
  PagingState,
  IntegratedPaging,
  SelectionState
} from '@devexpress/dx-react-grid'

import DateTypeProvider from './DateTypeProvider'
import CurrencyTypeProvider from './CurrencyTypeProvider'
import FileTypeProvider from './FileTypeProvider'
import PhoneTypeProvider from './PhoneTypeProvider'
import SelectTypeProvider from './SelectTypeProvider'
import LinkTypeProvider from './LinkTypeProvider'
import CodeTypeProvider from './CodeTypeProvider'
import SelectionAction from './SelectionAction'

const EditButtonTypeProvider = props => (
  <DataTypeProvider formatterComponent={withRouter(EditButton)} {...props} />
)

const LoadingState = ({ loading, columnCount }) => (
  <td
    colSpan={columnCount}
    style={{ textAlign: 'center', verticalAlign: 'middle' }}>
    <big>{loading ? <CircularProgress /> : <span>Nenhum dado.</span>}</big>
  </td>
)

const AddButton = ({ onExecute }) => (
  <div style={{ textAlign: 'center' }}>
    <IconButton color="primary" onClick={onExecute} title="Edit row">
      <AddIcon fontSize="small" />
    </IconButton>
  </div>
)

const EditButton = ({ onExecute }) => (
  <IconButton onClick={onExecute} title="Edit row">
    <EditIcon fontSize="small" />
  </IconButton>
)

const DeleteButton = ({ onExecute }) => (
  <IconButton onClick={onExecute} title="Delete row">
    <DeleteIcon fontSize="small" />
  </IconButton>
)

const CommitButton = ({ onExecute }) => (
  <IconButton onClick={onExecute} title="Save changes">
    <SaveIcon fontSize="small" />
  </IconButton>
)

const CancelButton = ({ onExecute }) => (
  <IconButton onClick={onExecute} title="Cancel changes">
    <CancelIcon fontSize="small" />
  </IconButton>
)

const commandComponents = {
  add: AddButton,
  edit: EditButton,
  delete: DeleteButton,
  commit: CommitButton,
  cancel: CancelButton
}

const Command = ({ id, onExecute }) => {
  const CommandButton = commandComponents[id]
  return <CommandButton onExecute={onExecute} />
}

const GridView = ({
  dense,
  editable,
  classes,
  rows,
  columns,
  extensions,
  validations,
  bands,
  grouping,
  loading,
  detailComponent,
  handleSave,
  handleUpdate,
  handleDelete,
  action
}) => {
  const [selection, changeSelection] = useState([])

  const getRowId = row => row.id
  const commitChanges = ({ added, changed, deleted }) => {
    console.log({ added, changed, deleted })
    if (added) {
      added.forEach(data => {
        handleSave(data)
      })
    }
    if (changed) {
      for (const id in changed) {
        if (changed.hasOwnProperty(id)) {
          const data = changed[id]
          data && handleUpdate({ id, data })
        }
      }
    }
    if (deleted) {
      deleted.forEach(id => {
        if (window.confirm('Deseja apagar o registro?')) {
          handleDelete(id)
        }
      })
    }
  }
  return (
    <MuiThemeProvider theme={createMuiTheme(theme)}>
      <Paper className={classes.root}>
        <Grid rows={rows} columns={columns} getRowId={getRowId}>
          {action && (
            <SelectionState
              selection={selection}
              onSelectionChange={selection => changeSelection(selection)}
            />
          )}
          <SortingState
            defaultSorting={filter(columns, 'sort').map(col => ({
              columnName: col.name,
              direction: col.sort
            }))}
          />
          <IntegratedSorting
            columnExtensions={filter(columns, { type: 'select' }).map(col => ({
              columnName: col.name,
              compare: (a, b) => (a[col.label] < b[col.label] ? -1 : 1)
            }))}
          />
          <SearchState />
          <RowDetailState />
          <IntegratedFiltering
            columnExtensions={filter(columns, { type: 'select' }).map(col => ({
              columnName: col.name,
              predicate: (value, filter, row) =>
                String(value[col.label])
                  .toLowerCase()
                  .includes(String(filter.value).toLowerCase())
            }))}
          />
          {grouping && <GroupingState grouping={grouping} />}
          {grouping && <IntegratedGrouping />}
          <PagingState
            defaultCurrentPage={0}
            defaultPageSize={!dense ? 10 : 5}
          />
          <IntegratedPaging />
          {editable && <EditButtonTypeProvider for={['id']} />}
          <DataTypeProvider
            for={filter(columns, { type: 'number' }).map(col => col.name)}
            availableFilterOperations={[
              'equal',
              'notEqual',
              'greaterThan',
              'greaterThanOrEqual',
              'lessThan',
              'lessThanOrEqual'
            ]}
          />
          <CodeTypeProvider
            for={filter(columns, { type: 'code' }).map(col => col.name)}
          />
          <LinkTypeProvider
            for={filter(columns, { type: 'link' }).map(col => col.name)}
          />
          <CurrencyTypeProvider
            for={filter(columns, { type: 'currency' }).map(col => col.name)}
          />
          <DateTypeProvider
            for={filter(columns, { type: 'date' }).map(col => col.name)}
            availableFilterOperations={[
              'contains',
              'greaterThanOrEqual',
              'lessThanOrEqual'
            ]}
          />
          <SelectTypeProvider
            for={filter(columns, { type: 'select' }).map(col => col.name)}
          />
          <FileTypeProvider
            for={filter(columns, { type: 'file' }).map(col => col.name)}
          />
          <PhoneTypeProvider
            for={filter(columns, { type: 'phone' }).map(col => col.name)}
          />
          {editable && (
            <EditingState
              onCommitChanges={commitChanges}
              columnExtensions={validations}
            />
          )}
          <Table
            columnExtensions={extensions}
            noDataCellComponent={() => (
              <LoadingState columnCount={columns.length} loading={loading} />
            )}
          />
          <TableHeaderRow showSortingControls />
          {editable && <TableEditRow />}
          {editable && (
            <TableEditColumn
              showAddCommand
              showEditCommand
              showDeleteCommand
              commandComponent={Command}
            />
          )}
          {bands && <TableBandHeader columnBands={bands} />}
          {grouping && <TableGroupRow />}
          {detailComponent && (
            <TableRowDetail contentComponent={detailComponent} />
          )}
          {!dense && <Toolbar />}
          {action && (
            <SelectionAction
              text={action.text}
              tooltip={action.tooltip}
              onClick={() => action.onClick(selection)}
            />
          )}
          {!dense && <SearchPanel />}
          <PagingPanel pageSizes={!dense ? [10, 25, 50, 100, 0] : [5, 10, 0]} />
          {action && <TableSelection selectByRowClick />}
        </Grid>
      </Paper>
    </MuiThemeProvider>
  )
}

GridView.defaultProps = {
  dense: false,
  rows: []
}

const styles = theme => ({
  root: {
    width: '100%',
    maxWidth: 1024,
    margin: 'auto',
    marginTop: 0,
    overflowX: 'auto'
  },
  grid: {
    '& tbody tr:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04) !Important',
      cursor: 'pointer'
    }
  }
})

export default compose(withStyles(styles))(GridView)
